import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { useState, useEffect } from 'react';
import Spacing from 'components/Spacing';
import PageSection from 'components/PageSection';
import useCompany from 'hooks/data/company/useCompany';
import { useUploadAllocations } from 'hooks/data/allocation/useUploadAllocations';
import {
  CircularProgress,
  useTheme,
  Grid,
  Tooltip,
  IconButton,
  Box,
  styled,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import {
  AppDatePicker,
  AppRadioButton,
  AppNumberInput,
  FieldRow,
} from 'components/FormElements';
import { NavLink } from 'react-router-dom';
import {
  createShareSplitAddRoute,
  createCompanyEditRoute,
} from 'adminConstants/routes';
import GridContainer from 'components/GridContainer';
import TrancheMultiSelect from 'components/TrancheMultiSelect';
import { Column, Row } from 'components/Layout';
import Button from 'components/Button';
import UploadAllocationSheet from './UploadAllocationSheet';
import AutoAllocations from './AutoAllocations';
import {
  DealDetailsFormValues,
  AutoAllocatingTranchesFormValues,
  FundAllocation,
} from './index';
import { useFunds } from 'hooks/data/fund/useFunds';
import { fundStatusMap, FundStatus } from 'adminConstants/fundProperties';
import { InitialTargetShareOption } from 'further-types/allocations';
import ExistingDraftAllocationStatus from 'components/ExistingDraftAllocationStatus';
import CompanyInfoDisplay, { Value } from './CompanyInfoDisplay';
import { ErrorMessage, WarningMessage } from './shared/StyledComponents';
import ButtonGroup from 'components/ButtonGroup';

const StyledIconButton = styled(IconButton)(() => ({
  padding: 0,
  marginLeft: 4,
}));

const DealDetailsForm: React.FC<{
  companyId: string;
  isNewShareClassCreated: boolean;
  onReview: () => void;
  onBack: () => void;
  handleClearAndRestart: () => void;
  setFundAutoAllocationResults: (
    fundAutoAllocationResults: Array<FundAllocation>,
  ) => void;
  formMethods: UseFormReturn<DealDetailsFormValues>;
  isAutoAllocation: boolean;
  setIsAutoAllocation: (value: boolean) => void;
  autoAllocationFormMethods: UseFormReturn<AutoAllocatingTranchesFormValues>;
  fundAutoAllocationResults: Array<FundAllocation>;
  initialTargetShareSelected: InitialTargetShareOption;
  setInitialTargetShareSelected: (value: InitialTargetShareOption) => void;
  resetInitialTargetShares: () => void;
  preservedAutoAllocationInput: Array<FundAllocation>;
}> = ({
  companyId,
  isNewShareClassCreated,
  onReview,
  onBack,
  handleClearAndRestart,
  setFundAutoAllocationResults,
  formMethods,
  isAutoAllocation,
  setIsAutoAllocation,
  autoAllocationFormMethods,
  fundAutoAllocationResults,
  initialTargetShareSelected,
  setInitialTargetShareSelected,
  resetInitialTargetShares,
  preservedAutoAllocationInput,
}) => {
  const theme = useTheme();
  const [isTemplateDownloaded, setIsTemplateDownloaded] = useState(false);
  const [showPreviousShareSplit, setShowPreviousShareSplit] = useState(false);
  const { data: investee } = useCompany(companyId, true);
  const { downloadTemplate } = useUploadAllocations();
  const {
    tranches: { data, isFetching: isLoadingTranches },
  } = useFunds({
    fundStatus: [fundStatusMap.closed, fundStatusMap.open],
    firmId: investee?.firmId,
    leanResponse: true,
    hasInvestments: true,
    fullyDeployed: false,
  });

  const tranchesList =
    data
      ?.map((tranche) => {
        return {
          ...tranche,
          label: (
            <>
              {tranche.firmId.firmName} {tranche.fundName}
              {tranche.hasDraftAllocation && (
                <ExistingDraftAllocationStatus
                  draft={tranche.hasDraftAllocation}
                  text="Draft"
                  marginX={theme.spacing(1)}
                  tooltipTitle="There is currently a live draft allocation for this tranche. Running a new auto-allocation or saving a new allocation upload will delete the current draft allocation."
                />
              )}
            </>
          ),
        };
      })
      ?.sort((item) => item.fundName?.toLowerCase()) ?? [];

  const { control, setValue, handleSubmit, watch } = formMethods;

  // Set form values once investee data is loaded, but only if they haven't been set by the user
  useEffect(() => {
    if (
      investee?.currentSharePrice &&
      !formMethods.getValues('dealSharePrice')
    ) {
      setValue('dealSharePrice', investee.currentSharePrice);
    }
  }, [investee, setValue, formMethods]);

  // Disable auto-allocation when deal share price is 0
  const dealSharePrice = watch('dealSharePrice');
  useEffect(() => {
    if (dealSharePrice === 0) {
      setIsAutoAllocation(false);
    }
  }, [dealSharePrice, setIsAutoAllocation]);

  // Check if any selected tranches are not closed
  const selectedTrancheIds = watch('trancheIds');
  const hasNonClosedTranches =
    selectedTrancheIds?.some((trancheId) => {
      const tranche = tranchesList.find((t) => t._id === trancheId);
      return tranche && tranche.fundStatus !== FundStatus.CLOSED;
    }) ?? false;

  // Disable auto-allocation if any selected tranche is not closed or if deal share price is 0
  useEffect(() => {
    if (hasNonClosedTranches && isAutoAllocation) {
      setIsAutoAllocation(false);
    }
  }, [hasNonClosedTranches, isAutoAllocation, setIsAutoAllocation]);

  const onSubmit = async (formValues: DealDetailsFormValues) => {
    // Prepare form data for the download template
    const formData = {
      selectedTrancheIds: formValues.trancheIds,
      selectedCompanyId: companyId,
      investmentDate: formValues.allocationDate?.toISOString() ?? '',
      dealSharePrice: formValues.dealSharePrice,
      firmId: investee?.firmId ?? '',
    };

    await downloadTemplate.mutateAsync(formData);
    setIsTemplateDownloaded(true);
  };

  const handleTemplateReset = () => {
    setIsTemplateDownloaded(false);
  };

  const showDownroundWarning =
    watch('dealSharePrice') &&
    investee?.currentSharePrice &&
    watch('dealSharePrice') < investee.currentSharePrice;

  const isAutoAllocationDisabled = dealSharePrice === 0 || hasNonClosedTranches;

  if (!investee) {
    return <CircularProgress />;
  }

  return (
    <GridContainer>
      <Grid size={{ sm: 12 }}>
        <FormProvider {...formMethods}>
          <Spacing>
            <PageSection title="Deal details">
              <Spacing>
                <Grid size={{ sm: 12, md: 12, lg: 9 }}>
                  <Spacing>
                    <CompanyInfoDisplay investee={investee} />
                    <FieldRow
                      title="Deal share price*"
                      centerTitle
                      tooltipText={
                        <>
                          This is the deal's agreed legal share price.{' '}
                          {!isNewShareClassCreated && (
                            <>
                              If there has been a share split since the
                              company’s last share price update, process this in
                              the share split function for this company{' '}
                              <NavLink
                                style={{ color: theme.palette.primary[400] }}
                                target="_blank"
                                to={createShareSplitAddRoute(companyId)}
                              >
                                here
                              </NavLink>{' '}
                              before processing a new allocation.
                            </>
                          )}
                        </>
                      }
                    >
                      <Controller
                        name="dealSharePrice"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <AppNumberInput
                            {...field}
                            placeholder="Enter deal share price"
                            required
                            min={0}
                            decimals={100}
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                            currencyStartAdornment
                            onChange={(value) => {
                              field.onChange(value);
                              handleTemplateReset();
                            }}
                          />
                        )}
                      />
                      {showDownroundWarning ? (
                        <WarningMessage>
                          This share price implies a down round for the company.
                          If a share split is the cause of this price
                          difference, please process this{' '}
                          <NavLink
                            style={{ color: theme.palette.primary[400] }}
                            target="_blank"
                            to={createShareSplitAddRoute(companyId)}
                          >
                            here
                          </NavLink>{' '}
                          before continuing with this allocation. If this is a
                          one-off transaction that does not affect company value
                          (e.g. bonus share issue), please visit the company
                          page after completing the allocation, to process a
                          revaluation.
                        </WarningMessage>
                      ) : null}
                    </FieldRow>

                    <FieldRow title="Investment date*" centerTitle>
                      <Controller
                        name="allocationDate"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <AppDatePicker
                            {...field}
                            required
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                            disableFuture
                            placeholder="Enter investment date"
                            onChange={(value) => {
                              field.onChange(value);
                              handleTemplateReset();
                              setShowPreviousShareSplit(false);
                            }}
                          />
                        )}
                      />
                      {showPreviousShareSplit && (
                        <ErrorMessage>
                          The investment date must be after this share class's
                          latest share split. To continue, choose a later date
                          or delete the share split in the company record{' '}
                          <NavLink
                            style={{
                              textDecoration: 'underline',
                              color: theme.palette.error.main,
                            }}
                            target="_blank"
                            to={createCompanyEditRoute(companyId)}
                          >
                            here
                          </NavLink>
                          .
                        </ErrorMessage>
                      )}
                    </FieldRow>

                    <FieldRow title="Tranche(s)*" centerTitle>
                      <Controller
                        name="trancheIds"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <TrancheMultiSelect
                              tranches={tranchesList}
                              isLoading={isLoadingTranches}
                              handleChangeSelectedTranches={(value) => {
                                setValue('trancheIds', value, {
                                  shouldValidate: true,
                                });
                                handleTemplateReset();
                              }}
                              value={field.value}
                              selectAllLabel="Select all tranches"
                              getIsDisabled={
                                isAutoAllocation
                                  ? (tranche) => {
                                      const trancheData = tranchesList.find(
                                        (t) => t._id === tranche._id,
                                      );
                                      return (
                                        trancheData?.fundStatus !==
                                        FundStatus.CLOSED
                                      );
                                    }
                                  : undefined
                              }
                              getDisabledTooltip={
                                isAutoAllocation
                                  ? () =>
                                      'This tranche is currently open. Only closed tranches can be selected for auto-allocation use.'
                                  : undefined
                              }
                            />
                            {error && (
                              <ErrorMessage>{error.message}</ErrorMessage>
                            )}
                          </>
                        )}
                      />
                    </FieldRow>

                    <FieldRow title="Allocation method" labelSpacing={10}>
                      <Column>
                        <AppRadioButton
                          name="allocationMethod"
                          label={<Value>Upload allocation spreadsheet</Value>}
                          value={false}
                          checked={!isAutoAllocation}
                          onChange={() => {
                            setIsAutoAllocation(false);
                            setShowPreviousShareSplit(false);
                          }}
                        />

                        <AppRadioButton
                          name="allocationMethod"
                          label={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                              }}
                            >
                              <Value disabled={isAutoAllocationDisabled}>
                                Auto-allocation
                              </Value>
                              {hasNonClosedTranches && (
                                <Tooltip
                                  title="One or more tranches selected are currently open for investment. Auto-allocation can only be used with closed tranches. Please unselect any open tranches before proceeding."
                                  arrow
                                  placement="right"
                                >
                                  <StyledIconButton size="small">
                                    <InfoOutlined
                                      fontSize="small"
                                      color="disabled"
                                    />
                                  </StyledIconButton>
                                </Tooltip>
                              )}
                            </Box>
                          }
                          disabled={isAutoAllocationDisabled}
                          value={true}
                          checked={isAutoAllocation}
                          onChange={() => {
                            setIsAutoAllocation(true);
                            handleTemplateReset();
                            setShowPreviousShareSplit(false);
                          }}
                        />
                      </Column>
                    </FieldRow>

                    {!isTemplateDownloaded && !isAutoAllocation && (
                      <Row justify="space-between">
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={onBack}
                        >
                          Back
                        </Button>
                        <ButtonGroup align="right">
                          {!isAutoAllocation && (
                            <Button
                              variant="text"
                              color="primary"
                              onClick={() => setIsTemplateDownloaded(true)}
                            >
                              Skip
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSubmit(onSubmit)}
                            loading={downloadTemplate.isPending}
                          >
                            Create
                          </Button>
                        </ButtonGroup>
                      </Row>
                    )}
                  </Spacing>
                </Grid>

                {isAutoAllocation && autoAllocationFormMethods && (
                  <>
                    <AutoAllocations
                      selectedCompanyId={companyId}
                      selectedTrancheIds={watch('trancheIds')}
                      dealSharePrice={watch('dealSharePrice')}
                      handleClearAndRestart={handleClearAndRestart}
                      onReview={onReview}
                      allocationDate={watch('allocationDate')}
                      formMethods={autoAllocationFormMethods}
                      fundAutoAllocationResults={fundAutoAllocationResults}
                      setFundAutoAllocationResults={
                        setFundAutoAllocationResults
                      }
                      initialTargetShareSelected={initialTargetShareSelected}
                      setInitialTargetShareSelected={
                        setInitialTargetShareSelected
                      }
                      resetInitialTargetShares={resetInitialTargetShares}
                      preservedAutoAllocationInput={
                        preservedAutoAllocationInput
                      }
                      setShowPreviousShareSplit={setShowPreviousShareSplit}
                    />
                  </>
                )}
              </Spacing>
            </PageSection>

            {isTemplateDownloaded && (
              <PageSection>
                <UploadAllocationSheet
                  onTemplateReset={handleTemplateReset}
                  selectedCompanyId={companyId}
                  isNewShareClassCreated={isNewShareClassCreated}
                  onReview={onReview}
                  setFundAutoAllocationResults={setFundAutoAllocationResults}
                  handleClearAndRestart={handleClearAndRestart}
                />
              </PageSection>
            )}
          </Spacing>
        </FormProvider>
      </Grid>
    </GridContainer>
  );
};

export default DealDetailsForm;
